import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Link,
  Navigate,
} from "react-router-dom";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import AnalyticsConsent from "@/components/AnalyticsConsent";
import { setLenis, scrollToTop } from "@/lib/scroll";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import ProjectDetail from "@/pages/ProjectDetail";
import About from "@/pages/About";
import Journal from "@/pages/Journal";
import Article from "@/pages/Article";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToTop();
  }, [pathname]);

  return null;
}

function ScrollbarManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    let color = "#FB9C06";

    if (pathname.startsWith("/service")) {
      color = "#3A6BE5";
    } else if (pathname.startsWith("/original")) {
      color = "#FF4C33";
    } else if (pathname.startsWith("/about")) {
      color = "#AD83D7";
    } else if (pathname.startsWith("/journal")) {
      color = "#DF2233";
    } else if (pathname.startsWith("/contact")) {
      color = "#ADC7D7";
    } else if (pathname.startsWith("/work")) {
      color = "#FB9C06";
    } else if (pathname.startsWith("/projects")) {
      color = "#FB9C06";
    }

    document.documentElement.style.setProperty(
      "--scrollbar-thumb",
      color
    );

    document.documentElement.style.scrollbarColor =
      `${color} transparent`;
  }, [pathname]);

  return null;
}

function NotFound() {
  return (
    <>
      <SEO title="Page Not Found" noindex />

      <div
        className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-16"
        data-testid="not-found-page"
      >
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
        404
      </p>

      <h1 className="font-display text-4xl font-extrabold tracking-tight">
        That reel isn't in the can.
      </h1>

      <Link
        to="/"
        className="rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-cream dark:bg-cream dark:text-ink"
        data-testid="not-found-home-link"
      >
        Back to the work
      </Link>
      </div>
    </>
  );
}

function App() {
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion) {
      setLenis(null);
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.15,
      smoothWheel: true,
    });

    setLenis(lenis);

    let rafId;

    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <AuthProvider>
      <BrowserRouter>
        <ScrollManager />
        <ScrollbarManager />

        <Navbar />

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/work"
              element={<Work key="all" category="all" />}
            />
            <Route
              path="/gallery"
              element={<Navigate to="/work" replace />}
            />
            <Route
              path="/original"
              element={<Work key="original" category="original" />}
            />
            <Route
              path="/service"
              element={<Work key="service" category="service" />}
            />
            <Route
              path="/projects/:slug"
              element={<ProjectDetail />}
            />
            <Route path="/about" element={<About />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<Article />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <Footer />
        <AnalyticsConsent />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
      </AuthProvider>
    </MotionConfig>
  );
}

export default App;
